/**
 * "Last 7 days" on the tractor page (SW-8879): readiness now with its reason
 * and since-when, runs this week, and the two lanes per day — production
 * readiness over lake activity — drawn from the daily cells. Read only: the
 * link opens Tractor Metrics with this tractor's customer and the week.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "styled-components";
import { TRACTOR_METRICS_ENABLED } from "../../utils/devFlags";
import { Button } from "../../components/ui/button";
import { fetchVehicleDays } from "../TractorMetrics/api";
import { segmentsFromCells } from "../TractorMetrics/cells";
import { todayIso } from "../TractorMetrics/time";
import { DailyRow } from "../TractorMetrics/types";
import { Card, Eyebrow } from "./styled";
import { tokens } from "./tokens";
import { Vehicle } from "./data";
import { READINESS_FIELDS } from "./configModel";

interface ReadinessWeekCardProps {
  /** Cognito ID token; absent in snapshot mode, where the card stays hidden. */
  token?: string;
  vehicle: Vehicle;
}

/** The zone the activity store keeps every tractor in today. */
const TRACTOR_TZ = "America/New_York";

/** YYYY-MM-DD, `days` before the local date `iso`. */
function shiftDay(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function dayLabel(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    day: "numeric",
  });
}

function sinceLabel(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: TRACTOR_TZ,
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

type NowTone = "ok" | "bad" | "muted";

/** What the record on this page says right now, and since when. */
function readinessNow(vehicle: Vehicle): {
  label: string;
  tone: NowTone;
  reason: string | null;
  since: string | null;
} {
  const value = vehicle.productionReadiness?.value ?? null;
  const blockers = vehicle.productionReadiness?.blockers ?? [];
  const latest = vehicle.history
    .filter((entry) =>
      Object.keys(entry.changes ?? {}).some((n) =>
        READINESS_FIELDS.includes(n),
      ),
    )
    .map((entry) => entry.effective_at ?? entry.at)
    .sort()
    .pop();
  const since = latest ? sinceLabel(latest) : null;
  if (value === "READY") {
    return { label: "Production ready", tone: "ok", reason: null, since };
  }
  if (value === "BLOCKED" || value === "NOT_READY") {
    return {
      label: "Not ready",
      tone: "bad",
      reason:
        blockers[0] ?? (value === "NOT_READY" ? "Marked not ready" : null),
      since,
    };
  }
  return {
    label: "No readiness mark",
    tone: "muted",
    reason: null,
    since: null,
  };
}

const ReadinessWeekCard: React.FC<ReadinessWeekCardProps> = ({
  token,
  vehicle,
}) => {
  const to = todayIso();
  const from = shiftDay(to, -6);
  const [rows, setRows] = useState<DailyRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const enabled = TRACTOR_METRICS_ENABLED && Boolean(token);

  const load = useCallback(() => {
    if (!enabled || !token) return;
    setError(null);
    fetchVehicleDays(token, vehicle.vehicleName, from, to).then(
      (res) => setRows(res.days),
      () => setError("The last 7 days did not load."),
    );
  }, [enabled, token, vehicle.vehicleName, from, to]);

  useEffect(() => {
    setRows(null);
    load();
  }, [load]);

  const days = useMemo(() => {
    const byDay = new Map((rows ?? []).map((r) => [r.local_day, r]));
    return Array.from({ length: 7 }, (_, i) => {
      const day = shiftDay(from, i);
      return { day, row: byDay.get(day) };
    });
  }, [rows, from]);

  if (!enabled) return null;

  const runs = (rows ?? []).reduce(
    (acc, r) => acc + Number(r.runs ?? r.rides ?? 0),
    0,
  );
  const now = readinessNow(vehicle);
  const name = vehicle.displayName ?? vehicle.vehicleName;
  const query = new URLSearchParams({ span: "7d", to });
  if (vehicle.customer) query.set("customer", vehicle.customer);

  return (
    <WeekCard>
      <Head>
        <Eyebrow as="h2">Last 7 days</Eyebrow>
        <Legend aria-label="Legend">
          <span>
            <Swatch className="f" /> Production ready
          </span>
          <span>
            <Swatch className="n" /> Not ready
          </span>
          <span>
            <Swatch className="b" /> Busy
          </span>
          <span>
            <Swatch className="a" /> Available
          </span>
          <span>
            <Swatch className="t" /> Not yet
          </span>
        </Legend>
      </Head>

      <Numbers>
        <Stat>
          <span>Now</span>
          <NowValue $tone={now.tone}>{now.label}</NowValue>
          {(now.reason || now.since) && (
            <Small>
              {now.reason}
              {now.reason && now.since ? " · " : ""}
              {now.since ? `since ${now.since}` : ""}
            </Small>
          )}
        </Stat>
        <Stat>
          <span>Runs this week</span>
          <strong>{rows ? runs : "…"}</strong>
          <Small>rides started</Small>
        </Stat>
      </Numbers>

      {error ? (
        <Notice role="alert">
          {error}{" "}
          <Button type="button" variant="ghost" size="sm" onClick={load}>
            Retry
          </Button>
        </Notice>
      ) : (
        <Days>
          {days.map(({ day, row }) => (
            <Day key={day}>
              <Cell role="img" aria-label={`${name} on ${day}`}>
                <Lane>
                  {segmentsFromCells(row?.cells?.readiness, "readiness").map(
                    (s, i) => (
                      <span
                        key={`r${i}`}
                        className={s.cls}
                        style={{ width: `${s.w}%` }}
                      />
                    ),
                  )}
                </Lane>
                <Lane $thin>
                  {segmentsFromCells(row?.cells?.activity, "activity").map(
                    (s, i) => (
                      <span
                        key={`a${i}`}
                        className={s.cls}
                        style={{ width: `${s.w}%` }}
                      />
                    ),
                  )}
                </Lane>
              </Cell>
              <DayLabel>{dayLabel(day)}</DayLabel>
            </Day>
          ))}
        </Days>
      )}

      <Foot>
        <span>
          Readiness from this record; activity from the data lake. Within a
          cell, left is 00:00 and right is 24:00.
        </span>
        <a href={`/metrics?${query.toString()}`}>Open in Tractor Metrics ↗</a>
      </Foot>
    </WeekCard>
  );
};

export default ReadinessWeekCard;

const WeekCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 18px 12px;
  min-width: 0;
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  gap: 6px 16px;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  font: 12px ${tokens.fontSans};
  color: ${tokens.slate};
`;

const Swatch = styled.span`
  display: inline-block;
  width: 11px;
  height: 11px;
  border-radius: 3px;
  margin-right: 5px;
  vertical-align: -1px;
  &.f {
    background: #8fd79f;
  }
  &.n {
    background: #c6262b;
  }
  &.b {
    background: #0a3fa8;
  }
  &.a {
    background: #8fb8ff;
  }
  &.t {
    border: 1px solid ${tokens.line};
    background: repeating-linear-gradient(135deg, #f1f2f4 0 3px, #fff 3px 5px);
  }
`;

const Numbers = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  > span:first-child {
    font: 500 11px ${tokens.fontMono};
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${tokens.slate};
  }
  > strong {
    font: 600 22px ${tokens.fontSans};
    font-variant-numeric: tabular-nums;
    color: ${tokens.ink};
  }
`;

const NowValue = styled.strong<{ $tone: NowTone }>`
  font: 600 18px ${tokens.fontSans};
  color: ${(p) =>
    p.$tone === "ok"
      ? tokens.readyText
      : p.$tone === "bad"
        ? tokens.groundedText
        : tokens.slate};
`;

const Small = styled.span`
  font: 12px ${tokens.fontSans};
  color: ${tokens.slate};
`;

const Days = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
`;

const Day = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Lane = styled.div<{ $thin?: boolean }>`
  display: flex;
  height: ${(p) => (p.$thin ? 10 : 16)}px;
  border-radius: 4px;
  overflow: hidden;
  background: #eef0f3;
  > span {
    display: block;
    height: 100%;
    flex: none;
  }
  > .f {
    background: #8fd79f;
  }
  > .n {
    background: #c6262b;
  }
  > .u {
    background: #d6d9de;
  }
  > .b {
    background: #0a3fa8;
  }
  > .a {
    background: #8fb8ff;
  }
  > .o {
    background: #eef0f3;
  }
  > .t {
    background: repeating-linear-gradient(135deg, #f1f2f4 0 4px, #fff 4px 7px);
  }
`;

const DayLabel = styled.span`
  font: 11px ${tokens.fontMono};
  color: ${tokens.slate};
  text-align: center;
`;

const Notice = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font: 13px ${tokens.fontSans};
  color: ${tokens.ink};
`;

const Foot = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px 16px;
  font: 12px ${tokens.fontSans};
  color: ${tokens.slate};
  a {
    font-weight: 600;
    color: ${tokens.blue};
    text-decoration: none;
    white-space: nowrap;
  }
`;
